/**
 * CAR-IA structural primitives (v3): SectionHeader, TrustMarkGrid, PriceBlock,
 * SpecRow. Composed by VehicleCard and the detail/feed screens.
 */
import React from 'react';
import { CheckIcon, StarIcon, ShieldIcon, FileCheckIcon, ShieldCheckIcon } from './icons';
import './caria-components.css';

/* ---------- SectionHeader ---------- */
export interface SectionHeaderProps {
  title: string;
  paren?: string;
  variant?: 'trust' | 'decision' | 'plain';
  /** Right-aligned trust % pill (e.g. "100%"). */
  /** Granted trust signals as a count, e.g. "3/4" — not a score (DEF-005). */
  trustSignals?: string;
  trustWarn?: boolean;
}
export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  paren,
  variant = 'plain',
  trustSignals,
  trustWarn,
}) => (
  <div
    className={`cia-section-title${variant !== 'plain' ? ` cia-section-title--${variant}` : ''}`}
  >
    <span>{title}</span>
    {paren && <span className="paren">({paren})</span>}
    {trustSignals && (
      <span className={`cia-trust-percent${trustWarn ? ' cia-trust-percent--warn' : ''}`}>
        <CheckIcon size={11} />
        {trustSignals}
      </span>
    )}
  </div>
);

/* ---------- TrustMarkGrid ---------- */
export type TrustMarkKey = 'tasado' | 'inspected' | 'carfax' | 'verificado';
export interface TrustMark {
  key: TrustMarkKey;
  label: string;
  on: boolean;
}
const MARK_ICON: Record<TrustMarkKey, React.FC<{ size?: number }>> = {
  tasado: StarIcon,
  inspected: ShieldIcon,
  carfax: FileCheckIcon,
  verificado: ShieldCheckIcon,
};
export const DEFAULT_TRUST_MARKS: TrustMark[] = [
  { key: 'tasado', label: 'Tasado', on: false },
  { key: 'inspected', label: 'Inspected', on: false },
  { key: 'carfax', label: 'Carfax', on: false },
  { key: 'verificado', label: 'Verificado', on: false },
];
export const TrustMarkGrid: React.FC<{ marks: TrustMark[] }> = ({ marks }) => (
  <div className="cia-trust-grid">
    {marks.map((m) => {
      const Icon = MARK_ICON[m.key];
      return (
        <div key={m.key} className={`cia-trust-mark ${m.on ? 'is-on' : 'is-off'}`}>
          <div className="cia-trust-mark-icon">
            <Icon size={16} />
          </div>
          <span className="cia-trust-mark-label">{m.label}</span>
        </div>
      );
    })}
  </div>
);

/* ---------- PriceBlock ---------- */
export interface PriceBlockProps {
  /** Primary price, formatted (e.g. "$23,650"). */
  usd: string;
  /** Secondary local price (e.g. "RD$ 1,420,000"). */
  secondary?: string;
  /** Chips row above the price (TasadorChip, DecisionChip, …). */
  chips?: React.ReactNode;
}
export const PriceBlock: React.FC<PriceBlockProps> = ({ usd, secondary, chips }) => (
  <div className="cia-price-block">
    {chips && <div className="cia-price-chips">{chips}</div>}
    <div className="cia-price-main">
      <span className="cia-price-usd">{usd}</span>
      {secondary && <span className="cia-price-rd">{secondary}</span>}
    </div>
  </div>
);
